import React, { type ChangeEvent } from 'react';

import { Key } from '../../../../../utils';
import isNil from '../../../../../utils/isNil';
import { Input, type InputProps } from '../../../../Input';
import { useSelectRootContext } from '../../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../../root/SelectRootFloatingContext';

import styles from './SelectDropdownSearchInput.module.scss';

export type SelectDropdownSearchInputProps = Omit<InputProps, 'value'>;

export const SelectDropdownSearchInput = ({
    onKeyDown,
    onFocus,
    onBlur,
    onChange,
    ...inputProps
}: SelectDropdownSearchInputProps) => {
    const {
        internalSearchValue,
        setInternalSearchValue,
        open,
        setOpen,
        searchContainerRef,
        setIsDropdownSearchFieldFocused,
        selection: { selectOptionByIndex },
        options: { activeIndex, setActiveIndex, findFirstEnabledIndex }
    } = useSelectRootContext();
    const { floatingInteractions } = useSelectRootFloatingContext();
    const events = floatingInteractions.getFloatingProps() as {
        onKeyDown: (e: React.KeyboardEvent) => void;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
        setInternalSearchValue(e.target.value);
        setActiveIndex(findFirstEnabledIndex());
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onKeyDown?.(e);

        if (e.key === Key.Enter && !isNil(activeIndex)) {
            e.preventDefault();
            selectOptionByIndex(activeIndex);
        }

        // allow list navigation (useListNavigation) to handle the event
        events.onKeyDown(e);
        if (e.key === Key.Escape) {
            setOpen(false);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsDropdownSearchFieldFocused(true);
        onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsDropdownSearchFieldFocused(false);
        onBlur?.(e);
    };

    return (
        <div className={styles.search} ref={searchContainerRef}>
            <Input
                {...inputProps}
                autoFocus={open}
                value={internalSearchValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        </div>
    );
};
